import React, { useEffect, useRef, memo } from "react"; 
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import StationCard from "./StationCard"; 
import { COLORS } from "./constants"; 

const StationList = memo(({
  stations,
  selectedStation,
  setSelectedStation,
  loading,
  error,
  allStations,
}) => {
  const listRef = useRef(null);
  const cardRefs = useRef({});

  useEffect(() => {
    if (selectedStation && cardRefs.current[selectedStation.id]) {
      cardRefs.current[selectedStation.id].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedStation]);

  return (
    <Box
      sx={{
        width: { xs: "100%", md: 360, lg: 400 },
        minWidth: { md: 340 },
        height: "100%",
        bgcolor: "#f8f9fa",
        borderRight: { md: `1px solid #eaeaea` },
        display: "flex",
        flexDirection: "column",
        order: { xs: 2, md: 1 },
        position: "relative",
        zIndex: 2,
        boxShadow: "4px 0 15px rgba(0,0,0,0.03)"
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 2,
          bgcolor: "#fff",
          borderBottom: `1px solid #f0f0f0`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Typography variant="h6" sx={{ fontSize: "1.25rem", fontWeight: 700, color: "#1a202c" }}>
            Nearby Stations
        </Typography>

        {!loading && !error && stations.length > 0 && (
          <Box 
            sx={{ 
                bgcolor: COLORS.primary, 
                color: "#fff", 
                px: 1.5, py: 0.5, 
                borderRadius: "12px", 
                fontSize: "1rem", 
                fontWeight: 700 
            }}
          >
            {stations.length} FOUND
          </Box>
        )}
      </Box>

      {/* Scrollable List */}
      <Box
        ref={listRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2,
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { 
              background: "#d1d5db", 
              borderRadius: "4px",
              "&:hover": { background: "#9ca3af" }
          },
        }}
      >
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" pt={8} gap={2}>
            <CircularProgress size={30} sx={{ color: COLORS.primary }} />
            <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>Scanning area...</Typography>
          </Box>
        ) : error ? (
          <Box px={2} pt={4}>
            <Alert severity="warning" sx={{ borderRadius: "12px" }}>{error}</Alert>
          </Box>
        ) : stations.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {stations.map((station, index) => (
              <Box 
                key={station.id} 
                ref={(el) => (cardRefs.current[station.id] = el)}
                sx={{ mb: 1 }}
              >
                <StationCard
                  station={station}
                  isSelected={selectedStation?.id === station.id}
                  onClick={() => setSelectedStation(station)}
                  allStations={allStations}
                  index={index + 1}
                />
              </Box>
            ))}

            <Typography sx={{ textAlign: 'center', fontSize: '1rem', color: '#999', mt: 2, mb: 4 }}>
                End of results
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "60%",
              textAlign: "center",
              px: 4,
              opacity: 0.7
            }}
          >
            <Box
              sx={{
                width: 80, height: 80, borderRadius: "50%",
                bgcolor: "#f0f2f5", mb: 2,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <SearchIcon sx={{ fontSize: 40, color: "#999" }} />
            </Box>
            <Typography sx={{ fontWeight: 600, color: "#444", mb: 0.5 }}>
                No stations found here
            </Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "#666" }}>
                Try increasing the search radius or typing a different location.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}); 

export default StationList;
